// CryptoAssetPage.js - Real-Time BitMEX Order Book Visualization
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { CandleChart } from '../components/CandleChart';
// --- CONFIG ---
// WebSocket URL - uses environment variable or defaults to direct BitMEX connection
// For production: Set REACT_APP_BITMEX_WS=/api/bitmex-ws/ws/bitmex
const WS_URL = process.env.REACT_APP_BITMEX_WS || '/api/bitmex-ws/ws/bitmex';

// Log which connection we're using
console.log('🔌 BitMEX WebSocket URL:', WS_URL);

// --- HELPER: Number Formatting ---
const formatPrice = (num) => num?.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || '0.0';
const formatSize = (num) => num?.toLocaleString('en-US') || '0';

// Helper to format BitMEX update for Lightweight Charts
const formatCandle = (d) => ({
  time: new Date(d.timestamp).getTime() / 1000, // Unix Timestamp
  open: d.open,
  high: d.high,
  low: d.low,
  close: d.close,
});

// --- COMPONENT: Order Book Row with Flash Animation ---
const OrderRow = ({ item, maxVolume, type, theme }) => {
  const prevSize = useRef(item.size);
  const [flash, setFlash] = useState('');

  // Detect size changes for that "live" feel
  useEffect(() => {
    if (item.size > prevSize.current) {
      setFlash('flash-up');
    } else if (item.size < prevSize.current) {
      setFlash('flash-down');
    } else {
      setFlash('');
    }
    
    prevSize.current = item.size;
    const timer = setTimeout(() => setFlash(''), 200);
    return () => clearTimeout(timer);
  }, [item.size]);

  // Calculate depth bar width
  const depthWidth = Math.min((item.size / maxVolume) * 100, 100);
  
  const color = type === 'bid' ? '#00bfa5' : '#ff5253';
  const bgBase = type === 'bid' ? 'rgba(0, 191, 165, 0.15)' : 'rgba(255, 82, 83, 0.15)';

  return (
    <div 
      style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        padding: '3px 12px',
        fontSize: '0.85rem',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        fontFamily: 'Roboto Mono, monospace',
        fontWeight: '500',
        transition: 'background-color 0.15s ease-out'
      }}
      className={flash}
    >
      {/* Depth Bar Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        [type === 'bid' ? 'right' : 'left']: 0,
        width: `${depthWidth}%`,
        height: '100%',
        backgroundColor: bgBase,
        zIndex: 0,
        transition: 'width 0.2s ease-out'
      }} />

      {/* Content */}
      <span style={{ zIndex: 1, color: theme.textSecondary }}>{formatSize(item.size)}</span>
      <span style={{ zIndex: 1, color: color, fontWeight: '600' }}>{formatPrice(item.price)}</span>
      
      <style>{`
        .flash-up { 
          background-color: rgba(0, 191, 165, 0.3) !important;
          animation: flash-pulse 0.2s ease-out;
        }
        .flash-down { 
          background-color: rgba(255, 82, 83, 0.2) !important;
          opacity: 0.7;
        }
        @keyframes flash-pulse {
          0% { opacity: 1; }
          50% { opacity: 0.7; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// --- HOOK: Hybrid Real-Time Candles (History + Live Ticks) ---
const useBitmexHybridCandles = (symbol, intervalSeconds = 60) => {
  const [candles, setCandles] = useState([]);
  const historyLoaded = useRef(false);
  
  // We keep a mutable reference to the latest candle to avoid dependency loops
  // inside the websocket closure
  const latestCandleRef = useRef(null);

  useEffect(() => {
    const bitmexSymbol = symbol === 'BTC' ? 'XBTUSD' : `${symbol}USD`;
    const ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      // 1. Subscribe to HISTORY (1m bins) AND LIVE TICKS (trade)
      // tradeBin1m gives us the past day of data instantly
      // trade gives us real-time updates for the current second
      ws.send(JSON.stringify({ 
        op: 'subscribe', 
        args: [`tradeBin1m:${bitmexSymbol}`, `trade:${bitmexSymbol}`] 
      }));
    };

    ws.onmessage = (event) => {
      const response = JSON.parse(event.data);
      if (!response.data) return;

      const { table, action, data } = response;

      // --- HANDLE HISTORY (Initial Load) ---
      if (table === 'tradeBin1m' && action === 'partial') {
        const history = data
          .map(formatCandle)
          .sort((a, b) => a.time - b.time);
        
        setCandles(history);
        historyLoaded.current = true;
        
        // Initialize our live ref with the last known candle
        if (history.length > 0) {
          latestCandleRef.current = history[history.length - 1];
        }
      }

      // --- HANDLE LIVE TICKS (Real-Time Updates) ---
      // This fires on EVERY trade. No 5s delay.
      if (table === 'trade' && (action === 'insert' || action === 'partial') && historyLoaded.current) {
        const trades = data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        trades.forEach(trade => {
            const price = trade.price;
            const time = new Date(trade.timestamp).getTime() / 1000;
            
            // Bucket by interval (e.g., 60s for 1m candles)
            const bucketTime = Math.floor(time / intervalSeconds) * intervalSeconds;
            
            let current = latestCandleRef.current;

            // Check if we need a NEW candle or update EXISTING
            if (!current || bucketTime > current.time) {
                // START NEW CANDLE
                const newCandle = {
                    time: bucketTime,
                    open: price,
                    high: price,
                    low: price,
                    close: price,
                };
                
                latestCandleRef.current = newCandle;
                
                // Append to state (Standard React State Update)
                setCandles(prev => [...prev, newCandle]);

            } else {
                // UPDATE EXISTING CANDLE (Real-time pulse)
                // We modify the ref directly for calculation, then push a COPY to state
                current.high = Math.max(current.high, price);
                current.low = Math.min(current.low, price);
                current.close = price;
                // Volume logic could go here if needed
                
                latestCandleRef.current = current;

                // FORCE UPDATE
                // We create a new array reference so React detects the change
                // But we only replace the last element
                setCandles(prev => {
                    const newPrev = [...prev];
                    newPrev[newPrev.length - 1] = { ...current }; // Clone to trigger re-render
                    return newPrev;
                });
            }
        });
      }
    };

    return () => ws.close();
  }, [symbol, intervalSeconds]);

  return { candles };
};

// --- NEW HOOK: Hybrid Live Candles ---
// Fetches history (1m bins) but updates LIVE using raw trades
const useBitmexLiveCandles = (symbol) => {
  const [candles, setCandles] = useState([]);
  const wsRef = useRef(null);
  const historyLoaded = useRef(false);
  const processedTrades = useRef(new Set()); // Dedup trades if needed

  useEffect(() => {
    const bitmexSymbol = symbol === 'BTC' ? 'XBTUSD' : `${symbol}USD`;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      // Subscribe to BOTH: 
      // 1. tradeBin1m (for the last 24h history)
      // 2. trade (for the real-time milliseconds updates)
      ws.send(JSON.stringify({ 
        op: 'subscribe', 
        args: [`tradeBin1m:${bitmexSymbol}`, `trade:${bitmexSymbol}`] 
      }));
    };

    ws.onmessage = (event) => {
      const response = JSON.parse(event.data);
      if (!response.data) return;
      const { table, action, data } = response;

      // 1. Handle Initial History (Load once)
      if (table === 'tradeBin1m' && action === 'partial' && !historyLoaded.current) {
        const formattedHistory = data.map(formatCandle).sort((a, b) => a.time - b.time);
        setCandles(formattedHistory);
        historyLoaded.current = true;
      }

      // 2. Handle Live Trades (Tick by Tick)
      // We ignore tradeBin1m updates/inserts because they are too slow.
      // We build the latest candle ourselves from 'trade'.
      if (table === 'trade' && (action === 'insert' || action === 'partial') && historyLoaded.current) {
         const trades = data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
         
         setCandles(prevCandles => {
            if (prevCandles.length === 0) return prevCandles;

            // Clone the list so we don't mutate state directly
            const newCandles = [...prevCandles];
            let lastCandle = { ...newCandles[newCandles.length - 1] };

            trades.forEach(trade => {
                const price = trade.price;
                const tradeTime = new Date(trade.timestamp).getTime() / 1000;
                
                // Calculate the 1-minute bucket for this trade
                // e.g., 10:05:23 -> 10:05:00
                const bucketTime = Math.floor(tradeTime / 60) * 60;

                // Case A: This trade belongs to a NEW candle
                if (bucketTime > lastCandle.time) {
                    // Push the completed last candle
                    newCandles[newCandles.length - 1] = lastCandle; 
                    
                    // Create new candle
                    lastCandle = {
                        time: bucketTime,
                        open: price,
                        high: price,
                        low: price,
                        close: price
                    };
                    newCandles.push(lastCandle);
                } 
                // Case B: This trade updates the CURRENT candle
                else if (bucketTime === lastCandle.time) {
                    lastCandle.high = Math.max(lastCandle.high, price);
                    lastCandle.low = Math.min(lastCandle.low, price);
                    lastCandle.close = price;
                    // We don't update 'open' because it's already set
                }
                // Case C: Old trade (ignore)
            });

            // Update the last candle in the array with our latest calculations
            newCandles[newCandles.length - 1] = lastCandle;
            return newCandles;
         });
      }
    };

    return () => ws.close();
  }, [symbol]);

  return { candles };
};

// --- HOOK: BitMEX WebSocket Order Book ---
const useBitmexOrderBook = (symbol) => {
  const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const bookMap = useRef(new Map());
  const wsRef = useRef(null);

  useEffect(() => {
    // BitMEX symbol mapping (they use XBTUSD instead of BTCUSD)
    const bitmexSymbol = symbol === 'BTC' ? 'XBTUSD' : `${symbol}USD`;
    
    const connectWebSocket = () => {
      try {
        console.log(`🔌 Connecting to ${WS_URL.includes('8960') ? 'Go Proxy' : 'BitMEX Direct'}...`);
        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('🟢 Connected to BitMEX WebSocket');
          setConnected(true);
          setError(null);
          // Subscribe to L2_25 (Top 25 levels) - perfect for frontend rendering
          ws.send(JSON.stringify({ 
            op: 'subscribe', 
            args: [`orderBookL2_25:${bitmexSymbol}`,
              `tradeBin1m:${bitmexSymbol}`      // NEW: 1-minute candles
            ] 
          }));
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Ignore info/subscription messages
            if (!data.data || data.table !== 'orderBookL2_25') return;

            const { action, data: updates } = data;

            if (action === 'partial') {
              // Initial snapshot
              bookMap.current.clear();
              updates.forEach(item => bookMap.current.set(item.id, item));
            } else if (action === 'insert' || action === 'update') {
              // Delta updates
              updates.forEach(item => {
                const existing = bookMap.current.get(item.id) || {};
                bookMap.current.set(item.id, { ...existing, ...item });
              });
            } else if (action === 'delete') {
              // Removal
              updates.forEach(item => bookMap.current.delete(item.id));
            }

            // Convert Map to Arrays for rendering
            const items = Array.from(bookMap.current.values());
            const bids = items
              .filter(i => i.side === 'Buy')
              .sort((a, b) => b.price - a.price)
              .slice(0, 25);
            const asks = items
              .filter(i => i.side === 'Sell')
              .sort((a, b) => a.price - b.price)
              .slice(0, 25);

            setOrderBook({ bids, asks });
          } catch (err) {
            console.error('Error parsing WebSocket message:', err);
          }
        };

        ws.onerror = (err) => {
          console.error('WebSocket error:', err);
          const errorMsg = WS_URL.includes('8960') 
            ? 'Go Proxy not running on port 8960' 
            : 'BitMEX connection error';
          setError(errorMsg);
        };

        ws.onclose = () => {
          console.log('🔴 Disconnected from BitMEX');
          setConnected(false);
          // Auto-reconnect after 3 seconds
          setTimeout(connectWebSocket, 3000);
        };

      } catch (err) {
        console.error('Failed to connect:', err);
        const errorMsg = WS_URL.includes('8960') 
          ? 'Go Proxy not available - Start with: go run main.go' 
          : 'Failed to connect to BitMEX';
        setError(errorMsg);
        setConnected(false);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [symbol]);

  return { orderBook, connected, error };
};

// --- MAIN PAGE ---
export default function CryptoAssetPage() {
  const { symbol } = useParams(); // e.g. "BTC", "ETH"
  const [isDark, setIsDark] = useState(true);
  const { orderBook, connected, error } = useBitmexOrderBook(symbol || 'BTC');
  // 2. ADD THIS NEW HOOK
  const { candles } = useBitmexLiveCandles(symbol || 'BTC');
//  const { candles } = useBitmexHybridCandles(symbol || 'BTC', 60);
 // const { candles } = useBitmexCandles(symbol || 'BTC');
  //const { candles } = useBitmexFastCandles(symbol || 'BTC', 5); // New 5s hook
  
  const theme = {
    background: isDark ? '#0a0a0a' : '#f9fafb',
    textPrimary: isDark ? '#e5e7eb' : '#111827',
    textSecondary: isDark ? '#6b7280' : '#9ca3af',
    cardBg: isDark ? '#141414' : '#ffffff',
    border: isDark ? '#262626' : '#e5e7eb',
  };

  // Calculate visualization stats
  const maxBidVol = Math.max(...orderBook.bids.map(b => b.size), 1);
  const maxAskVol = Math.max(...orderBook.asks.map(a => a.size), 1);
  const spread = orderBook.asks.length && orderBook.bids.length 
    ? orderBook.asks[0].price - orderBook.bids[0].price 
    : 0;
  const spreadPercent = orderBook.bids[0] 
    ? ((spread / orderBook.bids[0].price) * 100).toFixed(3)
    : '0.000';

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: theme.background, 
      color: theme.textPrimary, 
      fontFamily: 'Inter, sans-serif' 
    }}>
      {/* Silk Background */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <Silk 
          speed={20} 
          scale={1.2} 
          color={isDark ? "#1A1A2E" : "#E0E7FF"} 
          noiseIntensity={0.8}
        />
      </div>
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header 
          theme={theme} 
          isDark={isDark} 
          toggleTheme={() => setIsDark(!isDark)} 
          topTickers={[]} 
        />

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          
          {/* Back Button */}
          <button
            onClick={() => window.history.back()}
            style={{
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              color: theme.textPrimary,
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginBottom: '1.5rem',
              fontWeight: 'bold',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 191, 165, 0.1)';
              e.currentTarget.style.borderColor = '#00bfa5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.cardBg;
              e.currentTarget.style.borderColor = theme.border;
            }}
          >
            ← Voltar
          </button>

          {/* Top Bar: Asset Info & Connection Status */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h1 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                margin: 0,
                background: 'linear-gradient(to right, #00bfa5, #00d4ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {symbol} / USD
              </h1>
              <span style={{ 
                padding: '6px 12px', 
                borderRadius: '6px', 
                background: 'rgba(255, 165, 0, 0.15)', 
                color: '#ffa500', 
                fontSize: '0.75rem',
                fontWeight: 'bold',
                border: '1px solid rgba(255, 165, 0, 0.3)'
              }}>
                PERPETUAL SWAP
              </span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                background: connected ? '#00bfa5' : '#ff5253',
                boxShadow: connected 
                  ? '0 0 10px rgba(0, 191, 165, 0.5)' 
                  : '0 0 10px rgba(255, 82, 83, 0.5)',
                animation: connected ? 'pulse 2s infinite' : 'none'
              }} />
              <span style={{ fontSize: '0.85rem', color: theme.textSecondary, fontWeight: '500' }}>
                {connected ? 'Live Feed (BitMEX)' : error || 'Connecting...'}
              </span>
            </div>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'minmax(0, 1fr) 380px', 
            gap: '2rem' 
          }}>
            
            {/* LEFT: Candle Chart */}
<div style={{ 
  background: theme.cardBg, 
  border: `1px solid ${theme.border}`, 
  borderRadius: '12px', 
  height: '650px',
  overflow: 'hidden' // Important for the chart to stay inside
}}>
   {candles.length > 0 ? (
     <CandleChart 
       data={candles} 
       theme={{ ...theme, isDark }} // Pass isDark so chart knows grid color
       symbol={symbol} 
     />
   ) : (
     <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: theme.textSecondary 
     }}>
        Loading Chart Data...
     </div>
   )}
</div>
            {/* RIGHT: Order Book (BitMEX Style) */}
            <div style={{ 
              background: theme.cardBg, 
              border: `1px solid ${theme.border}`, 
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '650px'
            }}>
              
              {/* Header */}
              <div style={{ 
                padding: '1rem 1.25rem', 
                borderBottom: `1px solid ${theme.border}`,
                background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)'
              }}>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: theme.textPrimary
                }}>
                  📚 Order Book
                </h3>
              </div>
              
              {/* Show connection instructions if not connected and no data */}
              {!connected && orderBook.bids.length === 0 && orderBook.asks.length === 0 ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2rem',
                  textAlign: 'center',
                  gap: '1.5rem'
                }}>
                  <div style={{
                    fontSize: '3rem',
                    opacity: 0.3
                  }}>
                    ⚠️
                  </div>
                  
                  <div>
                    <h3 style={{ 
                      color: '#ffa500', 
                      marginBottom: '0.5rem',
                      fontSize: '1.2rem'
                    }}>
                      WebSocket Proxy Not Running
                    </h3>
                    <p style={{ 
                      color: theme.textSecondary,
                      fontSize: '0.9rem',
                      marginBottom: '1rem'
                    }}>
                      {error || 'Go Proxy not available on port 8960'}
                    </p>
                  </div>

                  <div style={{
                    background: isDark ? 'rgba(255,165,0,0.1)' : 'rgba(255,165,0,0.05)',
                    border: '1px solid rgba(255,165,0,0.3)',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'left',
                    fontSize: '0.85rem',
                    fontFamily: 'monospace',
                    maxWidth: '90%'
                  }}>
                    <div style={{ color: '#ffa500', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Quick Fix:
                    </div>
                    <div style={{ color: theme.textSecondary, lineHeight: '1.6' }}>
                      <div>1. cd bitmex-proxy</div>
                      <div>2. go run main.go</div>
                      <div style={{ marginTop: '0.5rem', opacity: 0.7 }}>
                        Attempting to reconnect...
                      </div>
                    </div>
                  </div>

                  <div style={{
                    fontSize: '0.75rem',
                    color: theme.textSecondary,
                    opacity: 0.6
                  }}>
                    Or use direct BitMEX connection (see docs)
                  </div>
                </div>
              ) : (
                <>
                  {/* Column Headers */}
                  <div style={{ 
                    padding: '0.75rem 1.25rem', 
                    fontSize: '0.7rem', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    color: theme.textSecondary,
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: `1px solid ${theme.border}`
                  }}>
                    <span>Size (USD)</span>
                    <span>Price (USD)</span>
                  </div>

                  {/* ASKS (Sells) - Red - Display in reverse order */}
                  <div style={{ 
                    flex: 1, 
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    minHeight: 0
                  }}>
                    {orderBook.asks.length > 0 ? (
                      orderBook.asks.slice(0, 18).map(ask => (
                       // --- START CHANGE ---
      // We wrap the row in a div with flexShrink: 0 to prevent squeezing
      <div key={ask.id} style={{ flexShrink: 0 }}>
        <OrderRow 
          item={ask} 
          maxVolume={maxAskVol} 
          type="ask" 
          theme={theme} 
        />
      </div>
                      ))
                    ) : (
                      <div style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: theme.textSecondary,
                        fontSize: '0.85rem'
                      }}>
                        Waiting for order book data...
                      </div>
                    )}
                  </div>

                  {/* SPREAD INDICATOR */}
                  <div style={{ 
                    padding: '1rem', 
                    textAlign: 'center', 
                    borderTop: `1px solid ${theme.border}`,
                    borderBottom: `1px solid ${theme.border}`,
                    background: isDark 
                      ? 'linear-gradient(to right, rgba(0,191,165,0.05), rgba(255,82,83,0.05))' 
                      : 'rgba(0,0,0,0.02)',
                    backdropFilter: 'blur(5px)'
                  }}>
                    <div style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold',
                      fontFamily: 'Roboto Mono, monospace',
                      color: theme.textPrimary,
                      marginBottom: '4px'
                    }}>
                      {orderBook.bids[0] ? formatPrice(orderBook.bids[0].price) : '---'}
                    </div>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: theme.textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}>
                      <span>Spread:</span>
                      <span style={{ 
                        color: '#ffa500',
                        fontWeight: '600',
                        fontFamily: 'Roboto Mono, monospace'
                      }}>
                        {spread > 0 ? `${spread.toFixed(1)} (${spreadPercent}%)` : '---'}
                      </span>
                    </div>
                  </div>

                  {/* BIDS (Buys) - Green */}
                  <div style={{ 
                    flex: 1, 
                    overflowY: 'auto',
                    minHeight: 0
                  }}>
                    {orderBook.bids.length > 0 ? (
                      orderBook.bids.slice(0, 18).map(bid => (
                        <OrderRow 
                          key={bid.id} 
                          item={bid} 
                          maxVolume={maxBidVol} 
                          type="bid" 
                          theme={theme} 
                        />
                      ))
                    ) : (
                      <div style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: theme.textSecondary,
                        fontSize: '0.85rem'
                      }}>
                        Waiting for order book data...
                      </div>
                    )}
                  </div>
                </>
              )}

            </div>
          </div>

        </main>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}